<template>
  <div class="gm-page impact-workbench">
    <div class="gm-topbar impact-topbar">
      <div class="impact-topbar__title">
        <div class="gm-eyebrow">Game Master / Balance Tools</div>
        <h1 class="gm-title">Skill Impact Visualization</h1>
      </div>

      <PathTabs
        v-if="catalog"
        :groups="catalog.groups"
        :active-id="activeGroupId"
        @select="activeGroupId = $event"
      />

      <div class="gm-topbar__actions">
        <button class="gm-btn gm-btn--sm" :disabled="loading" @click="loadCatalog(true)">
          Refresh
        </button>
      </div>
    </div>

    <div v-if="error" class="gm-page__body gm-empty">
      <div class="gm-empty__title">Failed to load the catalog</div>
      <p class="gm-empty__hint">{{ error }}</p>
      <button class="gm-btn gm-btn--primary" @click="loadCatalog(true)">Retry</button>
    </div>

    <div v-else-if="!catalog" class="gm-page__body gm-empty">
      <span class="dx-loader" aria-hidden="true"></span>
      <p class="gm-empty__hint">Loading schools and skills…</p>
    </div>

    <div v-else class="gm-page__body impact-body">
      <SkillImpactAnalysis
        :groups="catalog.groups"
        :active-group-id="activeGroupId"
        :loading="loading"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import PathTabs from '@/components/GameMaster/SkillVisualization/PathTabs.vue';
import SkillImpactAnalysis from '@/components/GameMaster/SkillVisualization/SkillImpactAnalysis.vue';
import skillImpactService from '@/services/GameMasterSkillImpactService.js';

const catalog = ref(null);
const activeGroupId = ref(null);
const loading = ref(false);
const error = ref(null);

async function loadCatalog(force = false) {
  loading.value = true;
  error.value = null;
  try {
    const loaded = await skillImpactService.loadCatalog({ force });
    catalog.value = loaded;
    if (!loaded.groups.some((group) => group.id === activeGroupId.value)) {
      activeGroupId.value = loaded.groups[0]?.id ?? null;
    }
  } catch (err) {
    console.error('Failed to load the skill catalog:', err);
    error.value = err?.message || 'An error occurred while loading schools and skills.';
  } finally {
    loading.value = false;
  }
}

onMounted(() => loadCatalog());
</script>

<style scoped>
/* Same shell as the archetype workbench: absolute inside the relatively
   positioned `main.main-content`, because the app shell sizes that element from
   its content — so a percentage height resolves against nothing and the page
   grows instead of its regions scrolling. Out of flow, the shell's 100vh
   decides the height and the three columns fill exactly one screen. */
.impact-workbench {
  position: absolute;
  inset: 0;
  min-height: 0;
  overflow: hidden;
}

.impact-topbar {
  flex: 0 0 auto;
  padding: var(--gm-gap-sm) var(--gm-gap-lg);
}

.impact-topbar__title {
  min-width: 0;
}

.impact-body {
  flex: 1;
  min-height: 0;
  padding: var(--gm-gap-sm) var(--gm-gap-sm) var(--gm-dock-clearance);
}

@media (max-width: 900px) {
  .impact-topbar {
    flex-wrap: wrap;
  }
}
</style>
